import { redirect } from "@tanstack/react-router"

import { fetchMe, isMembershipInactiveError, type MePublic } from "@/lib/me-api"
import { meKeys } from "@/lib/me-keys"

import type { RouterContext } from "./route-guards"

const signInPath = "/sign-in/$" as const
const signInParams = { _splat: "" } as const

type SessionJwtClaims = { sub: string | null; email: string | null }

/**
 * `sub` and `email` from Clerk's session JWT (unverified — for routing only, the backend
 * verifies). `sub` matches `userId` once `useAuth` has hydrated; `email` is the session-token
 * template claim the backend provisions from, so it is the same address `/api/users/me` returns.
 */
export function sessionJwtClaims(token: string): SessionJwtClaims {
  const none: SessionJwtClaims = { sub: null, email: null }
  const parts = token.split(".")
  if (parts.length < 2) return none
  try {
    const b64 = parts[1].replace(/-/g, "+").replace(/_/g, "/")
    const pad = b64.length % 4
    const padded = b64 + (pad ? "=".repeat(4 - pad) : "")
    const payload = JSON.parse(atob(padded)) as { sub?: unknown; email?: unknown }
    return {
      sub: typeof payload.sub === "string" && payload.sub.length > 0 ? payload.sub : null,
      email: typeof payload.email === "string" && payload.email.length > 0 ? payload.email : null,
    }
  } catch {
    return none
  }
}

/**
 * Prefetch + return GET `/api/users/me` for route guards (uses TanStack Query cache). Returns
 * `undefined` for the deactivated-membership 403 (FLO-166) instead of throwing — that error is
 * an expected app state, not a route failure, so `beforeLoad` should let the route render instead
 * of bubbling to the router's generic error screen. `AuthenticatedLayout` reads the same cached
 * query error via `useMeQuery` and renders the dedicated "Account deactivated" screen.
 */
export async function ensureMe(context: RouterContext): Promise<MePublic | undefined> {
  const token = await context.auth.getToken()
  if (!token) {
    throw redirect({ to: signInPath, params: signInParams })
  }
  // On full reload, `beforeLoad` can run before `useAuth().userId` is set; the session JWT
  // `sub` is still the same Clerk id `useMeQuery` will use (cache key alignment).
  const clerkUserId = sessionJwtClaims(token).sub ?? context.auth.userId
  if (!clerkUserId) {
    throw redirect({ to: signInPath, params: signInParams })
  }
  try {
    return await context.queryClient.ensureQueryData({
      queryKey: meKeys.detail(clerkUserId),
      queryFn: () => fetchMe(context.auth.getToken),
    })
  } catch (err) {
    if (isMembershipInactiveError(err)) return undefined
    throw err
  }
}
